import { ButtonHTMLAttributes, HTMLAttributes, ReactNode, useEffect, useState } from 'react'

import { cn } from '@/utils/style'

import { Button } from './Button'

type DataAttributes = {
  [key: `data-${string}`]: string | number | undefined
}

export interface ButtonRadioItem<T> {
  label: ReactNode
  value: T
  disabled?: boolean
  icon?: ReactNode
  buttonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'disabled' | 'onClick'> &
    DataAttributes
}

interface ButtonRadioProps<T> {
  items: ButtonRadioItem<T>[]
  className?: string
  rootProps?: Omit<HTMLAttributes<HTMLDivElement>, 'className'> & DataAttributes
  unselectedClassName?: string
  value?: T
  onChange?: (value: T) => void
  disabled?: boolean
  size?: 'small' | 'large'
}

export function ButtonRadio<T = any>({
  items,
  value,
  className,
  rootProps,
  onChange,
  disabled = false,
  unselectedClassName,
  size = 'large',
}: ButtonRadioProps<T>) {
  const [select, setSelect] = useState<T | undefined>(value ?? items?.[0]?.value)

  useEffect(() => {
    if (value) {
      setSelect(value)
    }
  }, [value])

  return (
    <div {...rootProps} className={cn('flex items-center gap-2', className)}>
      {items.map(({ label, value, disabled: itemDisabled, icon, buttonProps }, idx) => {
        const { className: buttonClassName, ...restButtonProps } = buttonProps ?? {}

        return (
          <Button
            {...restButtonProps}
            disabled={disabled || itemDisabled}
            key={idx}
            className={cn(
              't-sb',
              {
                't-sb': size === 'large',
                't-x-sb': size === 'small',
                'bg-btn-secondary/10 text-btn-secondary': value === select,
                'text-tertiary cursor-not-allowed': disabled || itemDisabled,
                [unselectedClassName ?? '']: value !== select,
              },
              buttonClassName,
            )}
            onClick={() => {
              if (disabled) return
              onChange?.(value)
              setSelect(value)
            }}
          >
            <span className={cn('flex items-center', { 'gap-2': !!icon })}>
              {icon}
              {label}
            </span>
          </Button>
        )
      })}
    </div>
  )
}
